import React, { useEffect, useState, useMemo } from 'react';
import {
  Package,
  AlertTriangle,
  DollarSign,
  RefreshCw,
  Loader2,
  PackageX,
  Boxes,
  AlertCircle,
  CheckCircle2,
  Plus,
  Building2,
  ChevronDown,
  Warehouse,
  TrendingUp,
  ShoppingCart,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import toast from 'react-hot-toast';
import inventoryitemService from '../../../services/inventoryitemService';
import branchService from '../../../services/branchService';
import { useAuth } from '../../../context/AuthContext';
import { P } from '@/config/permissionCodes';
import StockUpdateModal from './StockManagement/StockUpdateModal';


function SummaryCard({ icon: Icon, title, value, subtitle, color, iconBg, iconColor }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && (
            <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon size={24} className={iconColor} />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, message, iconBg, iconColor }) {
  return (
    <div className="text-center py-8">
      <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center mx-auto mb-3`}>
        <Icon size={24} className={iconColor} />
      </div>
      <p className="text-slate-900 font-medium mb-1">{title}</p>
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  );
}

// ─── Stock consumption date-range presets ──────────────────────────────────
const CONSUMPTION_PERIODS = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'thisWeek',  label: 'This Week' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
];

// Preset key → backend period enum. Manual date edits fall back to DATE_RANGE.
const PERIOD_ENUM = {
  today:     'TODAY',
  yesterday: 'YESTERDAY',
  thisWeek:  'THIS_WEEK',
  thisMonth: 'THIS_MONTH',
  lastMonth: 'LAST_MONTH',
};

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Returns { startDate, endDate } as 'YYYY-MM-DD' strings for the given preset.
const getPeriodRange = (period) => {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  switch (period) {
    case 'today': {
      const d = startOfDay(now);
      return { startDate: toYMD(d), endDate: toYMD(d) };
    }
    case 'yesterday': {
      const d = startOfDay(now);
      d.setDate(d.getDate() - 1);
      return { startDate: toYMD(d), endDate: toYMD(d) };
    }
    case 'thisWeek': {
      // Monday–Sunday week containing today.
      const d = startOfDay(now);
      const diffToMonday = (d.getDay() + 6) % 7; // 0 = Sun … 6 = Sat → days since Mon
      const start = new Date(d);
      start.setDate(d.getDate() - diffToMonday);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { startDate: toYMD(start), endDate: toYMD(end) };
    }
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { startDate: toYMD(start), endDate: toYMD(end) };
    }
    case 'lastMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end   = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: toYMD(start), endDate: toYMD(end) };
    }
    default:
      return { startDate: null, endDate: null };
  }
};

function AccessDenied() {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">

        <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
        <p className="text-sm text-slate-500">You don't have permission to view inventory items.</p>
      </div>
    </div>
  );
}

export default function StockOverview({ renderActions, title, actions }) {

  const { user, hasPermission } = useAuth();
  const isOwner = user?.userType === 'SUPER_USER';

  // ── Permission checks ────────────────────────────────────────────────────
  const canViewScreen    = hasPermission(P.INVENTORY.STOCK_OVERVIEW.SCREEN);
  const canUpdateStock   = hasPermission(P.INVENTORY.ITEMS.UPDATE_STOCK);
  // Consumption tab permission. Defaults to the screen permission if a dedicated
  // STOCK_CONSUMPTION permission code hasn't been added to permissionCodes yet.
  const canViewConsumption = P.INVENTORY?.STOCK_CONSUMPTION?.SCREEN
    ? hasPermission(P.INVENTORY.STOCK_CONSUMPTION.SCREEN)
    : canViewScreen;

  // ── Tabs ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(
    canViewConsumption ? 'consumption' : 'overview'
  ); // 'consumption' | 'overview'

  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Overview state
  const [lowStockItems, setLowStockItems] = useState([]);
  const [outOfStockItems, setOutOfStockItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [outOfStockCount, setOutOfStockCount] = useState(0);

  // Consumption state
  const [consumptionItems, setConsumptionItems] = useState([]);
  const [consumptionSummary, setConsumptionSummary] = useState(null); // from API
  const [consumptionSearch, setConsumptionSearch] = useState('');

  // ── Date filter: draft vs applied ─────────────────────────────────────────
  // draftRange   → what the two date inputs display. Editing it does NOT fetch.
  // appliedFilter → the single source of truth for the query. Only preset
  //                clicks and the Apply button write to it, so the report
  //                refreshes exactly on those two actions and nothing else.
  const initialRange = getPeriodRange('today');
  const [draftRange, setDraftRange] = useState({
    from: initialRange.startDate,
    to:   initialRange.endDate,
  });
  const [appliedFilter, setAppliedFilter] = useState({
    preset: 'today',                 // preset key, or null for custom range
    from:   initialRange.startDate,
    to:     initialRange.endDate,
  });
  const [activePreset, setActivePreset] = useState('today'); // highlight only

  // Preset click → unchanged behaviour: updates inputs AND applies immediately.
  const applyPreset = (key) => {
    const { startDate, endDate } = getPeriodRange(key);
    setDraftRange({ from: startDate, to: endDate });
    setActivePreset(key);
    setAppliedFilter({ preset: key, from: startDate, to: endDate });
  };

  // Manual date edit → draft only. Clears the preset highlight, no fetch.
  const handleDraftChange = (field, value) => {
    setDraftRange((r) => ({ ...r, [field]: value }));
    setActivePreset(null);
  };

  // Apply button → commits the draft as a custom DATE_RANGE query.
  const applyCustomRange = () => {
    if (!draftRange.from || !draftRange.to) return;
    setAppliedFilter({ preset: null, from: draftRange.from, to: draftRange.to });
  };

  // Apply is disabled only while either date is empty. It stays enabled after
  // applying, so users can adjust the range and re-apply any number of times.
  // (Re-clicking with the same range simply re-fetches — a manual refresh.)
  const canApplyCustomRange = !!draftRange.from && !!draftRange.to;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stockUpdateOpen, setStockUpdateOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches().then(result => {
      if (result && result.length > 1) setBranches(result);
    }).catch(console.error);
  }, [isOwner]);

  const showToast = (message, type = 'success') => {
    if (type === 'success') toast.success(message);
    else if (type === 'error') toast.error(message);
    else toast(message);
  };

  const handleUpdateStock = (item) => {
    if (!canUpdateStock) return;
    setSelectedItem(item);
    setStockUpdateOpen(true);
  };

  const handleStockUpdateComplete = async () => {
    setStockUpdateOpen(false);
    setSelectedItem(null);
    showToast('Stock updated successfully', 'success');
    await fetchData();
  };

  const normalizeList = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.data)) return res.data.data;
    if (res.data?.data && Array.isArray(res.data.data.items)) return res.data.data.items;
    if (res.data && Array.isArray(res.data.items)) return res.data.items;
    if (Array.isArray(res.items)) return res.items;
    return [];
  };

  const normalizeValue = (res, key) => {
    if (res == null) return 0;
    if (typeof res === 'number') return res;
    if (res.data != null && typeof res.data === 'number') return res.data;
    if (res.data && typeof res.data === 'object' && typeof res.data[key] === 'number') return res.data[key];
    if (res.data?.data && typeof res.data.data === 'object' && typeof res.data.data[key] === 'number') return res.data.data[key];
    if (res.data && typeof res.data.value === 'number' && key.toLowerCase().includes('value')) return res.data.value;
    const maybe = res.data?.[key] ?? res?.[key] ?? res.data?.data?.[key];
    const coerced = Number(maybe);
    if (!Number.isNaN(coerced)) return coerced;
    return 0;
  };

  const mapItem = (item) => ({
    id: item.id,
    name: item.name ?? item.itemName ?? item.title ?? 'Unnamed',
    categoryId: item.idCategory?.id ?? item.categoryId ?? null,
    categoryName: item.idCategory?.name ?? item.category?.name ?? item.categoryName ?? 'Uncategorized',
    idUom: item.uom?.id ?? item.unitOfMeasurement?.id ?? item.idUom ?? null,
    uomSymbol: item.uom?.symbol ?? item.unitOfMeasurement?.symbol ?? item.uomSymbol ?? '',
    currentStock: Number(item.currentStock ?? item.current_stock ?? 0),
    minimumStock: Number(item.minimumStock ?? item.minimum_stock ?? 0),
    unitCost: Number(item.unitCost ?? item.unit_cost ?? 0),
    isActive: item.isActive ?? true,
    isDeleted: item.isDeleted ?? false,
  });

  const mapConsumptionItem = (item) => {
    const opening  = Number(item.openingStock ?? item.opening_stock ?? 0);
    const purchase = Number(item.purchase ?? item.purchaseQty ?? item.purchased ?? item.purchase_qty ?? 0);
    const sale     = Number(item.sale ?? item.saleQty ?? item.sold ?? item.consumed ?? item.sale_qty ?? 0);
    const remainingRaw =
      item.remainingStock ?? item.remaining_stock ??
      item.closingStock ?? item.closing_stock ??
      item.currentStock ?? item.current_stock;
    const remaining = remainingRaw != null ? Number(remainingRaw) : (opening + purchase - sale);

    return {
      id: item.id ?? item.itemId ?? item.inventoryItemId,
      name: item.name ?? item.itemName ?? item.title ?? 'Unnamed',
      categoryName: item.idCategory?.name ?? item.category?.name ?? item.categoryName ?? 'Uncategorized',
      uomSymbol: item.uom?.symbol ?? item.unitOfMeasurement?.symbol ?? item.uomSymbol ?? item.unit ?? '',
      openingStock: opening,
      purchase,
      sale,
      remainingStock: remaining,
    };
  };

  // ── Overview data ──────────────────────────────────────────────────────────
  const fetchOverviewData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [
        totalValueRes,
        totalItemsRes,
        lowCountRes,
        outCountRes,
        lowItemsRes,
        outItemsRes,
      ] = await Promise.allSettled([
        inventoryitemService.getTotalValue(selectedBranchId),
        inventoryitemService.getTotalItems(selectedBranchId),
        inventoryitemService.getLowStockCount(selectedBranchId),
        inventoryitemService.getOutOfStockCount(selectedBranchId),
        inventoryitemService.getLowStockItems(selectedBranchId),
        inventoryitemService.getOutOfStockItems(selectedBranchId),
      ]);

      if (totalValueRes.status === 'fulfilled') setTotalValue(normalizeValue(totalValueRes.value, 'totalValue'));
      if (totalItemsRes.status === 'fulfilled') setTotalItems(normalizeValue(totalItemsRes.value, 'totalItems'));
      if (lowCountRes.status === 'fulfilled') setLowStockCount(normalizeValue(lowCountRes.value, 'lowStockCount'));
      if (outCountRes.status === 'fulfilled') setOutOfStockCount(normalizeValue(outCountRes.value, 'outOfStockCount'));
      if (lowItemsRes.status === 'fulfilled') setLowStockItems(normalizeList(lowItemsRes.value).map(mapItem));
      if (outItemsRes.status === 'fulfilled') setOutOfStockItems(normalizeList(outItemsRes.value).map(mapItem));

      const criticalFails = [totalItemsRes, totalValueRes, lowItemsRes, outItemsRes].filter(r => r.status === 'rejected');
      if (criticalFails.length === 4) throw new Error('Failed to load inventory statistics and lists.');
    } catch (err) {
      console.error('Failed to load inventory data:', err);
      setError('Failed to load inventory data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Consumption data ─────────────────────────────────────────────────────────
  // Reads only from appliedFilter — draft edits never reach this function.
  const fetchConsumptionData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Applied preset → send its enum only (backend resolves the range).
      // Applied custom range → send DATE_RANGE + explicit dates.
      const args = appliedFilter.preset && PERIOD_ENUM[appliedFilter.preset]
        ? { period: PERIOD_ENUM[appliedFilter.preset] }
        : { period: 'DATE_RANGE', startDate: appliedFilter.from, endDate: appliedFilter.to };
      const res = await inventoryitemService.getStockConsumptionOverview(args);
      // Response body is { summary, items }. Unwrap an axios response if present.
      const body = res?.data && (res.data.items || res.data.summary) ? res.data : res;
      setConsumptionItems(normalizeList(res).map(mapConsumptionItem));
      setConsumptionSummary(body?.summary ?? null);
    } catch (err) {
      console.error('Failed to load stock consumption data:', err);
      setError('Failed to load stock consumption data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Dispatch to the correct fetch based on the active tab.
  const fetchData = async () => {
    if (activeTab === 'consumption') return fetchConsumptionData();
    return fetchOverviewData();
  };

  // Refreshes only when the APPLIED filter changes (preset click or Apply
  // button), the tab switches, or the branch changes. Draft date edits do not
  // appear in the dependency list, so they can never trigger a fetch.
  useEffect(() => {
    if (activeTab === 'consumption') {
      // Need both ends of the applied range before hitting the API.
      if (!appliedFilter.preset && (!appliedFilter.from || !appliedFilter.to)) return;
      fetchConsumptionData();
    } else {
      fetchOverviewData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId, activeTab, appliedFilter]);

  useEffect(() => {
    if (typeof renderActions === 'function') {
      renderActions({ onRefresh: () => fetchData() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const computedTotalItems = totalItems || (lowStockItems.length + outOfStockItems.length);
    const healthyCount = computedTotalItems - (lowStockCount + outOfStockCount);
    return {
      totalItems: computedTotalItems,
      totalValue: totalValue || 0,
      lowStockCount,
      outOfStockCount,
      healthyCount: healthyCount > 0 ? healthyCount : 0,
      lowStockItems: lowStockItems.sort((a, b) =>
        (a.currentStock / (a.minimumStock || 1)) - (b.currentStock / (b.minimumStock || 1))
      ),
      outOfStockItems: outOfStockItems.sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [totalItems, totalValue, lowStockCount, outOfStockCount, lowStockItems, outOfStockItems]);

  const consumptionStats = useMemo(() => {
    const q = consumptionSearch.trim().toLowerCase();
    const filtered = q
      ? consumptionItems.filter(i =>
          i.name.toLowerCase().includes(q) || i.categoryName.toLowerCase().includes(q))
      : consumptionItems;

    // Prefer the API's summary; fall back to counting the loaded items.
    const s = consumptionSummary;
    return {
      items: [...filtered].sort((a, b) => a.name.localeCompare(b.name)),
      totalItems: s?.totalItems ?? consumptionItems.length,
      restockedCount: s?.restockedItems ?? consumptionItems.filter(i => i.purchase > 0).length,
      soldCount: s?.soldItems ?? consumptionItems.filter(i => i.sale > 0).length,
      depletedCount: s?.depletedItems ?? consumptionItems.filter(i => i.remainingStock <= 0).length,
    };
  }, [consumptionItems, consumptionSummary, consumptionSearch]);

  // Screen-level guard (kept after hooks to respect the Rules of Hooks).
  if (!canViewScreen) return <AccessDenied />;

  return (
    <div className="flex flex-col h-full bg-slate-50 -m-6">
      {/* Header (persistent across tabs) */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {/* Tab toggle */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              {canViewConsumption && (
                <button
                  onClick={() => setActiveTab('consumption')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    activeTab === 'consumption'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Stock Consumption
                </button>
              )}
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Stock Overview
              </button>
            </div>

            {/* Contextual chips */}
            {activeTab === 'overview' ? (
              <div className="hidden md:flex items-center gap-3 text-sm">
                <span className="text-slate-500">{stats.totalItems} items</span>
                {stats.lowStockCount > 0 && (
                  <span className="flex items-center gap-1 text-amber-600">
                    <AlertTriangle size={14} />
                    {stats.lowStockCount} low
                  </span>
                )}
                {stats.outOfStockCount > 0 && (
                  <span className="flex items-center gap-1 text-red-600">
                    <PackageX size={14} />
                    {stats.outOfStockCount} out
                  </span>
                )}
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-3 text-sm">
                <span className="text-slate-500">{consumptionStats.totalItems} items</span>
                {consumptionStats.depletedCount > 0 && (
                  <span className="flex items-center gap-1 text-red-600">
                    <PackageX size={14} />
                    {consumptionStats.depletedCount} depleted
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {branches.length > 1 && (
              <div className="relative">
                <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={selectedBranchId ?? ''}
                  onChange={(e) => setSelectedBranchId(e.target.value || null)}
                  className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 appearance-none cursor-pointer"
                >
                  <option value="">All Branches</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            )}
            <button
              onClick={fetchData}
              disabled={isLoading}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 size={40} className="text-teal-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-500">
              Loading {activeTab === 'consumption' ? 'consumption' : 'inventory'} data...
            </p>
          </div>
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={32} className="text-red-500" />
            </div>
            <p className="text-slate-900 font-medium mb-2">Error Loading Data</p>
            <p className="text-slate-500 text-sm mb-4">{error}</p>
            <button onClick={fetchData} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium inline-flex items-center gap-2">
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        </div>
      ) : activeTab === 'overview' ? (
        /* ─────────────────────────── Stock Overview ─────────────────────────── */
        <div className="flex-1 overflow-y-auto p-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard icon={Boxes} title="Total Items" value={stats.totalItems}
              subtitle={`${stats.healthyCount} in healthy stock`} color="text-slate-900"
              iconColor="text-teal-600" iconBg="bg-teal-100" />
            <SummaryCard icon={DollarSign} title="Total Stock Value"
              value={`₹${Number(stats.totalValue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
              subtitle="Current inventory value" color="text-slate-900"
              iconColor="text-emerald-600" iconBg="bg-emerald-100" />
            <SummaryCard icon={AlertTriangle} title="Low Stock" value={stats.lowStockCount}
              subtitle="Below minimum level"
              color={stats.lowStockCount > 0 ? "text-amber-600" : "text-slate-900"}
              iconColor="text-amber-600" iconBg="bg-amber-100" />
            <SummaryCard icon={PackageX} title="Out of Stock" value={stats.outOfStockCount}
              subtitle="Need immediate restock"
              color={stats.outOfStockCount > 0 ? "text-red-600" : "text-slate-900"}
              iconColor="text-red-600" iconBg="bg-red-100" />
          </div>

          {/* Two Tables Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Low Stock Items Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 bg-amber-50">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={18} className="text-amber-600" />
                  <h2 className="font-semibold text-slate-900">Low Stock Items</h2>
                  <span className="ml-auto text-sm text-amber-600 font-medium">
                    {stats.lowStockItems.length} item{stats.lowStockItems.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {stats.lowStockItems.length === 0 ? (
                <EmptyState icon={CheckCircle2} title="All Good!" message="No items are running low"
                  iconBg="bg-green-100" iconColor="text-green-600" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Category</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Current</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Minimum</th>
                        {/* Only show Action column if user can update stock */}
                        {canUpdateStock && (
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Action</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {stats.lowStockItems.map((item) => (
                        <tr key={item.id} className="hover:bg-amber-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <span className="font-medium text-slate-900">{item.name}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-500">{item.categoryName}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-semibold text-amber-600">
                              {item.currentStock.toFixed(2)} {item.uomSymbol}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm text-slate-500">
                              {item.minimumStock.toFixed(2)} {item.uomSymbol}
                            </span>
                          </td>
                          {canUpdateStock && (
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleUpdateStock(item)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors border border-teal-200"
                              >
                                <Plus size={14} />
                                Stock
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Out of Stock Items Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 bg-red-50">
                <div className="flex items-center gap-2">
                  <PackageX size={18} className="text-red-600" />
                  <h2 className="font-semibold text-slate-900">Out of Stock Items</h2>
                  <span className="ml-auto text-sm text-red-600 font-medium">
                    {stats.outOfStockItems.length} item{stats.outOfStockItems?.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {stats.outOfStockItems.length === 0 ? (
                <EmptyState icon={CheckCircle2} title="All Good!" message="No items are out of stock"
                  iconBg="bg-green-100" iconColor="text-green-600" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Category</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Minimum</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Unit Cost</th>
                        {/* Only show Action column if user can update stock */}
                        {canUpdateStock && (
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Action</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {stats.outOfStockItems.map((item) => (
                        <tr key={item.id} className="hover:bg-red-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <span className="font-medium text-slate-900">{item.name}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-500">{item.categoryName}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm text-slate-500">
                              {item.minimumStock.toFixed(2)} {item.uomSymbol}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-medium text-slate-900">
                              ₹{item.unitCost.toFixed(2)}
                            </span>
                          </td>
                          {canUpdateStock && (
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleUpdateStock(item)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors border border-teal-200"
                              >
                                <Plus size={14} />
                                Stock
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ────────────────────────── Stock Consumption ────────────────────────── */
        <div className="flex-1 overflow-y-auto p-6">
          {/* Filter bar */}
          <div className="bg-white rounded-xl border border-slate-200 mb-4 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center gap-2 text-slate-500">
              <SlidersHorizontal size={16} />
              <span className="text-sm font-medium">Filters</span>
            </div>
            <div className="p-3 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 flex-wrap">
                {CONSUMPTION_PERIODS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => applyPreset(p.key)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                      activePreset === p.key
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={draftRange.from}
                  max={draftRange.to || undefined}
                  onChange={(e) => handleDraftChange('from', e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
                <span className="text-slate-400 text-sm">to</span>
                <input
                  type="date"
                  value={draftRange.to}
                  min={draftRange.from || undefined}
                  onChange={(e) => handleDraftChange('to', e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                />
                {/* Custom range fetches only when Apply is clicked */}
                <button
                  onClick={applyCustomRange}
                  disabled={!canApplyCustomRange}
                  className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>

          {/* Summary Cards (counts — quantities are not summed because items may use different units) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard icon={Boxes} title="Total Items" value={consumptionStats.totalItems}
              subtitle="Tracked this period" color="text-slate-900"
              iconColor="text-teal-600" iconBg="bg-teal-100" />
            <SummaryCard icon={TrendingUp} title="Restocked" value={consumptionStats.restockedCount}
              subtitle="Items with purchases" color="text-slate-900"
              iconColor="text-emerald-600" iconBg="bg-emerald-100" />
            <SummaryCard icon={ShoppingCart} title="Sold" value={consumptionStats.soldCount}
              subtitle="Items with sales" color="text-slate-900"
              iconColor="text-indigo-600" iconBg="bg-indigo-100" />
            <SummaryCard icon={PackageX} title="Depleted" value={consumptionStats.depletedCount}
              subtitle="Remaining at or below zero"
              color={consumptionStats.depletedCount > 0 ? "text-red-600" : "text-slate-900"}
              iconColor="text-red-600" iconBg="bg-red-100" />
          </div>

          {/* Consumption Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Warehouse size={18} className="text-teal-600" />
                  <h2 className="font-semibold text-slate-900">Stock Consumption</h2>
                  <span className="text-sm text-slate-500 font-medium">
                    {consumptionStats.items.length} item{consumptionStats.items.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="relative ml-auto">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={consumptionSearch}
                    onChange={(e) => setConsumptionSearch(e.target.value)}
                    placeholder="Search item or category..."
                    className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none w-full sm:w-64"
                  />
                </div>
              </div>
            </div>

            {consumptionStats.items.length === 0 ? (
              <EmptyState icon={Boxes}
                title={consumptionSearch ? 'No matches' : 'No consumption data'}
                message={consumptionSearch
                  ? 'No items match your search.'
                  : 'There is no stock consumption to show for this period.'}
                iconBg="bg-slate-100" iconColor="text-slate-500" />
            ) : (
              <div className="overflow-x-auto">
                {/* table-fixed + explicit widths → equal, content-independent
                    column spacing. Item gets 24%; the four numeric columns get
                    an even 19% each, closing the gap after the Item column. */}
                <table className="w-full table-fixed">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="w-[24%] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Item</th>
                      <th className="w-[19%] px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Opening Stock</th>
                      <th className="w-[19%] px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Purchase</th>
                      <th className="w-[19%] px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Sale</th>
                      <th className="w-[19%] px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Remaining Stock</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {consumptionStats.items.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-slate-900 truncate" title={item.name}>{item.name}</span>
                            <span className="text-xs text-slate-400 truncate" title={item.categoryName}>{item.categoryName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-slate-600">
                            {item.openingStock.toFixed(2)} {item.uomSymbol}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`text-sm font-medium ${item.purchase > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {item.purchase > 0 ? '+' : ''}{item.purchase.toFixed(2)} {item.uomSymbol}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`text-sm font-medium ${item.sale > 0 ? 'text-indigo-600' : 'text-slate-400'}`}>
                            {item.sale > 0 ? '−' : ''}{item.sale.toFixed(2)} {item.uomSymbol}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`text-sm font-semibold ${item.remainingStock <= 0 ? 'text-red-600' : 'text-slate-900'}`}>
                            {item.remainingStock.toFixed(2)} {item.uomSymbol}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stock Update Modal — only accessible if permitted */}
      {stockUpdateOpen && selectedItem && canUpdateStock && (
        <StockUpdateModal
          item={selectedItem}
          uomSymbol={selectedItem.uomSymbol || ''}
          onSave={handleStockUpdateComplete}
          onClose={() => { setStockUpdateOpen(false); setSelectedItem(null); }}
        />
      )}
    </div>
  );
}